/**
 * coverageCamera.ts — decides whether the loaded-coverage extent is a trustworthy
 * opening camera, or whether the map should open on the national default (T-308).
 *
 * WHY THIS EXISTS
 * GET /api/coverage returns `extent` as a single server-side ST_Extent UNION over every
 * loaded tract. That union answers "where is our data", which is NOT the same question as
 * "where should the camera open". The two answers diverge the moment coverage includes a
 * non-contiguous region: the union of the mainland and Hawaii is a bbox whose centre is
 * open Pacific, so fitting it frames the ocean and every CONUS user pays a zoom to get
 * back to the mainland (T-308: Hawaii entering coverage did exactly this).
 *
 * Alaska is the reason this is keyed on the CLASS rather than clamped to Hawaii: the
 * Aleutians cross the antimeridian, so an AK union extent is roughly [-179, 18, +179, 71]
 * and fitting it frames the whole planet. Hawaii is the mild version of that bug. Keying
 * on non-contiguity means AK never reaches fitBounds at all, so this cannot re-break when
 * the next non-contiguous region lands.
 *
 * THE RULE
 *   - all-contiguous coverage      -> fit the extent. A compact deployment still opens
 *                                     tight on its own region (preserves T-092 A1).
 *   - contiguous + non-contiguous  -> null. MapView's existing null contract opens the
 *                                     CONUS national default; the island stays one
 *                                     deliberate zoom/pan away instead of costing every
 *                                     user a bad default.
 *   - exclusively non-contiguous   -> fit the extent. A HI-only deployment must open on
 *                                     HI; falling back to CONUS would open on a region
 *                                     with no data at all.
 *
 * KNOWN TRADE-OFF: a compact MAINLAND + non-contiguous deployment (e.g. NJ+HI) opens on
 * the CONUS default rather than tight on NJ. The union bbox destroyed the NJ-only
 * sub-extent before the client ever saw it, so the client cannot recover it. The precise
 * fix, if that deployment shape ever exists, is server-side: return a contiguous_extent
 * alongside extent from getCoverage (apps/api/src/db/queries/coverage.ts) and pass it in
 * here — this function is the seam, and the call site would not change shape.
 */
import type { CoverageState } from './api'

/**
 * State/territory FIPS codes that are NOT part of the contiguous United States. A union
 * bbox spanning any of these plus the mainland is unusable as a camera (see module docs).
 *   02 Alaska · 15 Hawaii · 60 American Samoa · 66 Guam · 69 Northern Mariana Islands
 *   72 Puerto Rico · 78 US Virgin Islands
 */
const NON_CONTIGUOUS_FIPS: ReadonlySet<string> = new Set(['02', '15', '60', '66', '69', '72', '78'])

/** True when this coverage row is a non-contiguous state/territory. */
function isNonContiguous(state: CoverageState): boolean {
  // FIPS arrives from a CHAR(2) column and can be space-padded; normalise before matching
  // so a padded '15 ' is never mistaken for contiguous coverage and trusted as a camera.
  return NON_CONTIGUOUS_FIPS.has(state.state_fips.trim())
}

/**
 * The bounds the map should fit on open, or null to use the national (CONUS) default.
 *
 * @param states  Loaded regions from GET /api/coverage (read for their FIPS).
 * @param extent  The union extent [west, south, east, north] of that coverage, or null.
 */
export function initialCameraBounds(
  states: CoverageState[],
  extent: [number, number, number, number] | null,
): [number, number, number, number] | null {
  // No data loaded (or coverage failed to resolve) — MapView's CONUS default is the answer.
  if (!extent || states.length === 0) return null

  const nonContiguous = states.filter(isNonContiguous)

  // Coverage is entirely non-contiguous (e.g. a HI-only deployment): the union extent is
  // the only meaningful camera there is, and CONUS would open on empty water.
  if (nonContiguous.length === states.length) return extent

  // Mixed coverage: the union spans mainland->island, so it is not a camera. Fall back.
  if (nonContiguous.length > 0) return null

  // All contiguous: the union extent is exactly the region to open on.
  return extent
}
